import { z } from 'zod';
// eslint-disable-next-line local-rules-imports/prefer-root-alias-imports -- Build configuration consumes this schema before Vite aliases exist.
import { modelSourceRepositorySchema, isModelSourceSegment } from '../../../01-models/llama-cpp-browser-model-launch';

export const modelDestinationSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('opfs') }).strict(),
  z.object({ kind: z.literal('host'), directoryId: z.string().min(1).max(255) }).strict(),
]);
export type ModelDestination = z.infer<typeof modelDestinationSchema>;

export function destinationKey({ destination }: { destination: ModelDestination | undefined }): string {
  const target = destination ?? { kind: 'opfs' };
  switch (target.kind) {
  case 'opfs': return 'opfs';
  case 'host': return `host/${encodeURIComponent(target.directoryId)}`;
  default: {
    const exhaustive: never = target;
    throw new Error(String(exhaustive));
  }
  }
}

/** Validate the public segment separately: suffixes and encoded folder names
 * are not directory IDs and must not inherit their length constraint.
 */
export function parsePublicHostModelReference({ modelId }: { modelId: string }): { alias: string, repository: string, selector: string | undefined } {
  const match = /^host\/([^/]+)\/(.+)$/.exec(modelId);
  if (!match) {
    throw new Error('Invalid public linked model reference');
  }
  const alias = decodeURIComponent(match[1]!);
  if (encodeURIComponent(alias) !== match[1]) {
    throw new Error('Invalid public linked model reference');
  }
  // Public selectors are readable relative names. Only the root alias is encoded;
  // a literal '%2F' in a filename must not turn into a path separator here.
  const value = match[2]!;
  const colon = value.indexOf(':');
  const repository = modelSourceRepositorySchema.parse(colon < 0 ? value : value.slice(0, colon));
  const selector = colon < 0 ? undefined : value.slice(colon + 1);
  if (selector !== undefined && !selector.split('/').every(name => isModelSourceSegment({ name }))) {
    throw new Error('Invalid public linked model selector');
  }
  return { alias, repository, selector };
}

export function isHostDestination(destination: ModelDestination | undefined): destination is Extract<ModelDestination, { kind: 'host' }> {
  if (!destination) {
    return false;
  }
  switch (destination.kind) {
  case 'host': return true;
  case 'opfs': return false;
  default: {
    const exhaustive: never = destination;
    throw new Error(String(exhaustive));
  }
  }
}

export const TEST_ONLY = {
};
